import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Computer as ComputerIcon,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';

interface Server {
  id: string;
  name: string;
  description: string;
  status: 'online' | 'offline';
  lastSeen: string | null;
  data: any;
  system: any;
  createdAt: string;
}

interface ServerManagerProps {
  onServerSelect?: (server: Server) => void;
}

const ServerManager: React.FC<ServerManagerProps> = ({ onServerSelect }) => {
  const [servers, setServers] = useState<Server[]>([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [connectionDialogOpen, setConnectionDialogOpen] = useState(false);
  const [connectionCommand, setConnectionCommand] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  
  const [newServer, setNewServer] = useState({
    name: '',
    description: ''
  });

  const loadServers = useCallback(async () => {
    try {
      const response = await apiService.getServers();
      setServers(response.data);
    } catch (error) {
      console.error('Sunucular yüklenirken hata:', error);
      showSnackbar('Sunucular yüklenirken hata oluştu', 'error');
    }
  }, []);

  useEffect(() => {
    loadServers();
  }, [loadServers]);

  const handleAddServer = async () => {
    try {
      const response = await apiService.addServer(newServer);
      setServers([...servers, response.data.server]);
      setConnectionCommand(response.data.connectionCommand.command);
      setAddDialogOpen(false);
      setConnectionDialogOpen(true);
      setNewServer({ name: '', description: '' });
      showSnackbar('Sunucu başarıyla eklendi', 'success');
    } catch (error) {
      console.error('Sunucu ekleme hatası:', error);
      showSnackbar('Sunucu eklenirken hata oluştu', 'error');
    }
  };

  const handleDeleteServer = async (serverId: string) => {
    if (!window.confirm('Bu sunucuyu silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      await apiService.deleteServer(serverId);
      setServers(servers.filter(s => s.id !== serverId));
      showSnackbar('Sunucu silindi', 'success');
    } catch (error) {
      console.error('Sunucu silme hatası:', error);
      showSnackbar('Sunucu silinirken hata oluştu', 'error');
    }
  };

  const handleShowConnection = async (serverId: string) => {
    try {
      const response = await apiService.getServerConnection(serverId);
      setConnectionCommand(response.data.command);
      setConnectionDialogOpen(true);
    } catch (error) {
      console.error('Bağlantı komutu alma hatası:', error);
      showSnackbar('Bağlantı komutu alınırken hata oluştu', 'error');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    showSnackbar('Komut panoya kopyalandı', 'success');
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
        return 'success';
      case 'offline':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'online':
        return 'Çevrimiçi';
      case 'offline':
        return 'Çevrimdışı';
      default:
        return status;
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Hiç';
    return new Date(dateString).toLocaleString('tr-TR');
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Sunucu Yönetimi ({servers.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
        >
          Sunucu Ekle
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Sunucu</TableCell>
              <TableCell>Durum</TableCell>
              <TableCell>Son Görülme</TableCell>
              <TableCell>Süreçler</TableCell>
              <TableCell>İşlemler</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {servers.map((server) => (
              <TableRow key={server.id} hover>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {server.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {server.description || 'Açıklama yok'}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={getStatusText(server.status)}
                    color={getStatusColor(server.status) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(server.lastSeen)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {server.data?.processes?.length || 0}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Bağlantı Komutu">
                      <IconButton
                        size="small"
                        onClick={() => handleShowConnection(server.id)}
                      >
                        <ComputerIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Sil">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteServer(server.id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Yeni Sunucu Dialogu */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Yeni Sunucu Ekle</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Sunucu Adı"
            fullWidth
            variant="outlined"
            value={newServer.name}
            onChange={(e) => setNewServer({ ...newServer, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Açıklama (Opsiyonel)"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={newServer.description}
            onChange={(e) => setNewServer({ ...newServer, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>İptal</Button>
          <Button onClick={handleAddServer} variant="contained">
            Ekle
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bağlantı Komutu Dialogu */}
      <Dialog open={connectionDialogOpen} onClose={() => setConnectionDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Sunucu Bağlantı Komutu</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Bu komutu uzak sunucuda çalıştırarak PM2 Cloud Agent'ı kurabilirsiniz.
          </Alert>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              fullWidth
              value={connectionCommand}
              variant="outlined"
              multiline
              rows={3}
              InputProps={{ readOnly: true }}
            />
            <IconButton onClick={() => copyToClipboard(connectionCommand)}>
              <CopyIcon />
            </IconButton>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConnectionDialogOpen(false)}>Kapat</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ServerManager;
